<template>
    <div v-if="catalogStore.getOption('view') === 'grid'">
        <table v-if="catalogStore.products.length > 0" class="table table-responsive align-middle catalog-table table-sm">
            <thead>
            <tr>
                <th></th>
                <th>{{ $t('Item') }}</th>
                <th v-if="enableBuyBack" class="text-end d-none d-md-table-cell">{{ $t('We buy')}}</th>
                <th class="text-center" nowrap>{{ $t(getPremiumColumnName()) }}</th>
                <th class="text-end">{{ $t('Weight') }}</th>
                <th class="text-center">{{ $t('Quantity') }}</th>
                <th class="text-end nowrap">{{ $t('Price') }}</th>
                <th class="text-center"></th>
                <th class="text-center"></th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(product, index) in catalogStore.products" :class="{'selected': cartStore.hasProduct(product.id)}" :ref="'product.' + product.id">
                <td>
                    <ProductImage :product="product"/>
                </td>
                <td>
                    <NuxtLink :to="product.url" class="me-2">{{ product.name}}</NuxtLink>
                    <ProductBadges :absolute="!isMobile" :product="product" />
                    <div>
                        <ProductStockIndicator :product="product"/>
                    </div>
                </td>
                <td class="text-end d-none d-md-table-cell nowrap" v-if="enableBuyBack">
                    <span v-if="product.buy_back_price > 0">{{ $formatter.percent(product.buy_back_price_premium_percent) }}&percnt;<br>{{ $formatter.price(product.buy_back_price_premium_value) }}</span>
                </td>
                <template v-if="product.shop_availability && product.metal_id && product.gross_price > 0 && product.premium_value !== null">
                    <td class="nowrap text-end">
                        <span>{{getPremiumPercentValue(product)}}&percnt;<br>{{getPremiumValue(product)}}</span>
                    </td>
                </template>
                <template v-else>
                    <td></td>
                </template>
                <td class="nowrap text-end">{{ weightStore.format(product.fine_weight) }}</td>
                <td>
                    <div class="d-flex justify-content-center" v-if="product.shop_availability">
                        <input class="quantity-input form-control form-control-sm text-center wp-50"
                               :class="{'input-error' : product.has_error}"
                               :tabindex="index + 2"
                               :title="quantityLimitReached(product) ? $t('Max quantity is ' + product.max_quantity) :
                                        (!validateFractionalQuantity(product) ? $t('Cannot be fractional') : null)"
                               :value="computedQuantities.find(q => q.id === product.id).quantity"
                               @keyup="updateQuantityInput(product.id, $event)"
                               @keydown.down.enter.prevent="changeTabIndex(index+3)"
                               @keydown.up.prevent="changeTabIndex(index+1)"
                               maxlength="5">
                    </div>
                </td>
                <td class="nowrap text-end price">
                    <span v-if="product.shop_availability && product.gross_price" v-text="!product.has_error && cartStore.hasProduct(product.id) && product.totalPrice ? $formatter.price(product.totalPrice) : $formatter.price(product.gross_price)"></span>
                </td>
                <td class="text-center ps-4">
                    <WatchlistButton :product_id=product.id />
                </td>
                <td class="text-center">
                    <template v-if="product.shop_availability">
                        <a v-if="cartStore.hasProduct(product.id)" @click.prevent="removeProductFromCart(product.id)" href="#"><img class="size-18" src="@img/icons/delete.svg" alt="remove"></a>
                        <a v-else @click.prevent="updateQuantity(product.id, 1)" href="#"><img class="size-18 add-icon" src="@img/icons/plus.svg" height="18" alt="add"></a>
                    </template>
                </td>
            </tr>
            </tbody>
        </table>
    </div>
    <div v-else class="pics-view row">
        <div v-for="product in catalogStore.products" class="col-12 col-lg-6 col-xl-4 mb-5" :ref="'product.' + product.id">
            <Thumb :product="product" />
        </div>
    </div>
    <div v-if="catalogStore.products.length <= 0">{{ $t('No results') }}</div>
</template>

<script setup>
const ProductImage = defineAsyncComponent(() => import('~/components/Product/ProductImage.vue'));

import {useMobileDetector} from "~/composables/useMobileDetector";
const ProductStockIndicator = defineAsyncComponent(() => import("~/components/Product/ProductStockIndicator.vue"));
const ProductBadges = defineAsyncComponent(() => import("~/components/Product/Page/ProductBadges.vue"));
const WatchlistButton = defineAsyncComponent(() => import("~/components/Product/Buttons/WatchlistButton.vue"));
const Thumb = defineAsyncComponent(() => import('~/components/Product/Thumbs/ProductThumb.vue'));

const catalogStore = useCatalogStore();
const cartStore = useCartStore();
const settingsStore = useSettingsStore();
const weightStore = useWeightStore();
const quantitiesStore = useProductQuantityStore();

const isMobile = useMobileDetector();

const computedQuantities = computed(() => {
    return catalogStore.products.map(product => {
        return {
            id: product.id,
            quantity: cartStore.getProductQuantity(product.id) || '',
        };
    });
});

const enableBuyBack = settingsStore.bool('products.show_product_buy_back_price');
const { $formatter } = useNuxtApp();

const getPremiumColumnName = () => enableBuyBack && !isMobile ? 'We sell' : 'Premium';

const getPremiumValue = (product) => {
    let value = product.premium_value;
    if (value <= 0) {
        value = 0;
    }
    return $formatter.price(value);
};

const getPremiumPercentValue = (product) => {
    let value = product.premium_percent;
    if (value <= 0) {
        value = 0;
    }
    return $formatter.percent(value);
};

const quantityLimitReached = (product)  => {
    if (isNaN(product.max_quantity) || product.max_quantity === null) {
        return false;
    }

    return cartStore.getProductQuantity(product.id) > product.max_quantity;
};

const validateFractionalQuantity = (product) => {
    let quantity = cartStore.getProductQuantity(product.id);
    if (!product.fract_quantity && typeof quantity !== 'undefined' && !Number.isInteger(Number(quantity))) {
        return false;
    }
    return true;
};

const updateQuantityInput = (product_id, e) => {
    let quantity = parseFloat(e.target.value);
    if (isNaN(quantity)) {
        quantity = null;
    }
    if (quantity <= 0) {
        return;
    }

    updateQuantity(product_id, quantity);
};

const updateQuantity = async (product_id, quantity) => {
    let product = catalogStore.products.find(x => x.id === product_id);
    if (!product) {
        return;
    }

    if (quantity === cartStore.getProductQuantity(product_id)) {
        return;
    }

    //update storage
    quantitiesStore.setQuantity(product.id, quantity);
    await cartStore.storeUpdateSave(product_id, quantity);
    updatePrice(product);

    // Focus the quantity  TODO:
    // $(this.$refs['product.' + product_id]).find('input.quantity-input').trigger('focus');
}

const updatePrice = (product) => {
    const quantityPrices = product.quantity_prices;
    const quantity = cartStore.getProductQuantity(product.id);
    let gross_price = product.raw_gross_price;

    if (quantityPrices.length > 0) {

        const foundPrice = quantitiesStore.getPriceBasedOnQuantity(product, quantity);

        if (foundPrice) {
            gross_price = foundPrice.raw_gross_price;
            product.premium_percent = foundPrice.premium_percent;
            product.premium_value = foundPrice.premium_value;
        }
    }
    product.totalPrice = gross_price * quantity;
};

// TODO:
const changeTabIndex = (index) => {
    // let el = $('input[tabindex=' + index + ']');
    // if (el.length > 0) {
    //     el.trigger('focus');
    // }
};

const removeProductFromCart = (product_id) => {
    cartStore.remove(product_id);
    quantitiesStore.setQuantity(product_id, 1);
};

</script>
